import { useId, useRef, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Icon,
  Modal,
  V2Badge,
  V2Button,
} from '@sakhtar/design-system/react';
import { useAiProvider } from '../connect/AiProviderContext.js';
import { browserPaymentRecoveryStore } from '../connect/paymentRecovery.js';
import { useAiResource } from '../live/useAiResource.js';
import {
  calculateTokensForToman,
  formatIntegerString,
  formatThousandsPersian,
  MIN_CUSTOM_TOPUP_TOKENS,
  parseDigits,
  topupAmount,
} from './pricing.js';
import './buy-credit-modal.css';

export interface BuyCreditModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSuccess?: () => void;
  /** Where the gateway returns (the wallet page). Defaults to the current path. */
  readonly walletPath?: string;
}

/**
 * مودال «خرید شارژ هوش مصنوعی»
 * مطابق دیزاین‌سیستم V2 ساختار:
 * - بسته‌های آماده قابل انتخاب با نمایش شفاف قیمت، تعداد توکن و نرخ
 * - گزینهٔ مبلغ دلخواه با ورودی اعداد فارسی/انگلیسی، جداکنندهٔ هزارگان و محاسبه زندهٔ توکن
 * - اعتبارسنجی حداقل ۱٬۰۰۰٬۰۰۰ تومان بدون سقف تجاری
 * - دکمه اصلی «پرداخت و افزایش اعتبار» با پشتیبانی کامل از وضعیت‌های در حال پردازش و خطا
 */
export function BuyCreditModal({ open, onClose, walletPath }: BuyCreditModalProps) {
  const provider = useAiProvider();
  const packages = useAiResource((signal) => provider.getWalletPackages({ signal }), [provider]);

  const [selectedPackageId, setSelectedPackageId] = useState<string>('pro');
  const [useCustom, setUseCustom] = useState(false);
  const [customDigits, setCustomDigits] = useState<string>('');
  const [customTouched, setCustomTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customInputRef = useRef<HTMLInputElement | null>(null);
  const customInputId = useId();

  if (!open) return null;

  const pkgList = packages.state.status === 'ready' ? packages.state.data.packages : [];
  const selectedPkg =
    pkgList.find((p) => p.id === selectedPackageId) ??
    pkgList.find((p) => p.id === 'pro') ??
    pkgList[0];

  // Custom amount validation (in tokens)
  let customError: string | null = null;
  let customValid = false;
  let customTokens = 0n;

  if (useCustom) {
    if (!customDigits) {
      if (customTouched) {
        customError = 'حداقل سهمیهٔ شارژ ۵٬۰۰۰٬۰۰۰ توکن است.';
      }
      customValid = false;
    } else {
      const tokensBig = BigInt(customDigits);
      if (tokensBig < MIN_CUSTOM_TOPUP_TOKENS) {
        customError = 'حداقل سهمیهٔ شارژ ۵٬۰۰۰٬۰۰۰ توکن است.';
        customValid = false;
      } else {
        customError = null;
        customValid = true;
        customTokens = tokensBig;
      }
    }
  }

  // Active purchase details (converted to Toman for API/gateway)
  const effectiveAmountToman = useCustom
    ? (customValid ? (customTokens / 5n).toString() : '')
    : (selectedPkg?.amountToman ?? '');

  const validatedAmount =
    effectiveAmountToman && packages.state.status === 'ready'
      ? topupAmount(effectiveAmountToman, packages.state.data.bounds, {
          allowUnlimitedMax: useCustom,
        })
      : null;

  const canPay =
    !submitting &&
    packages.state.status === 'ready' &&
    (useCustom ? customValid && validatedAmount !== null : selectedPkg !== undefined);

  const activeTokens = useCustom
    ? customTokens
    : selectedPkg?.tokenCount
      ? BigInt(selectedPkg.tokenCount)
      : calculateTokensForToman(selectedPkg?.amountToman ?? '0');

  const handleCustomChange = (e: ChangeEvent<HTMLInputElement>) => {
    const parsed = parseDigits(e.target.value);
    setCustomDigits(parsed);
    setCustomTouched(true);
    setUseCustom(true);
  };

  const handleSelectPackage = (pkgId: string) => {
    setUseCustom(false);
    setSelectedPackageId(pkgId);
    setError(null);
  };

  const handleSelectCustom = () => {
    setUseCustom(true);
    setError(null);
    setTimeout(() => {
      customInputRef.current?.focus();
    }, 50);
  };

  const handleStartPayment = async () => {
    if (!canPay || !validatedAmount) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await provider.createTopupCheckout({
        amountToman: validatedAmount.amountToman,
      });
      browserPaymentRecoveryStore(walletPath ?? window.location.pathname).remember(res.orderId);
      window.location.href = res.paymentUrl;
    } catch (err: unknown) {
      setSubmitting(false);
      setError(
        err instanceof Error
          ? err.message
          : 'خطا در برقراری ارتباط با درگاه پرداخت. لطفاً دوباره تلاش کنید.',
      );
    }
  };

  return (
    <Modal
      open={open}
      title="خرید شارژ هوش مصنوعی"
      onClose={onClose}
      footer={
        <div className="ai-topup-modal__footer">
          <V2Button
            variant="secondary"
            size="md"
            onClick={onClose}
            disabled={submitting}
          >
            انصراف
          </V2Button>
          <V2Button
            variant="primary"
            size="md"
            icon="cart"
            onClick={handleStartPayment}
            disabled={!canPay}
          >
            {submitting ? 'در حال اتصال به درگاه…' : 'پرداخت و افزایش اعتبار'}
          </V2Button>
        </div>
      }
    >
      <div className="ai-topup-modal__content">
        <p className="ai-topup-modal__intro">
          بستهٔ مورد نظر خود را انتخاب کرده یا مبلغ دلخواه وارد نمایید. سهمیهٔ توکن پس از پرداخت
          موفق بانکی فوراً به کیف پول هوش مصنوعی شرکت افزوده می‌شود.
        </p>

        {packages.state.status === 'error' ? (
          <Alert tone="danger" title="سرویس شارژ در دسترس نیست">
            <p>{packages.state.error.message}</p>
            <div style={{ marginTop: 8 }}>
              <V2Button type="button" size="sm" variant="secondary" onClick={packages.reload}>
                تلاش مجدد
              </V2Button>
            </div>
          </Alert>
        ) : null}

        {error ? (
          <Alert tone="danger" title="خطا در اتصال به درگاه">
            <p>{error}</p>
            <div style={{ marginTop: 8 }}>
              <V2Button
                type="button"
                size="sm"
                variant="primary"
                onClick={handleStartPayment}
                disabled={!canPay}
              >
                تلاش مجدد
              </V2Button>
            </div>
          </Alert>
        ) : null}

        {packages.state.status === 'loading' ? (
          <div className="ai-topup-modal__loading" role="status">
            در حال دریافت بسته‌های شارژ…
          </div>
        ) : (
          <div className="ai-topup-options">
            {/* بسته‌های آماده */}
            <div className="ai-topup-section-title">بسته‌های پیشنهادی</div>
            <div className="ai-topup-pkg-grid" role="radiogroup" aria-label="بسته‌های آماده شارژ">
              {pkgList.map((pkg) => {
                const isSelected = !useCustom && selectedPkg?.id === pkg.id;
                const tokenStr = pkg.tokenCount
                  ? formatIntegerString(pkg.tokenCount)
                  : formatIntegerString(calculateTokensForToman(pkg.amountToman).toString());
                return (
                  <button
                    type="button"
                    key={pkg.id}
                    className={`ai-topup-pkg-card ${isSelected ? 'ai-topup-pkg-card--active' : ''}`}
                    onClick={() => handleSelectPackage(pkg.id)}
                    role="radio"
                    aria-checked={isSelected}
                  >
                    <div className="ai-topup-pkg-card__top">
                      <div className="ai-topup-pkg-card__radio" aria-hidden="true">
                        {isSelected ? <span className="ai-topup-pkg-card__radio-inner" /> : null}
                      </div>
                      <span className="ai-topup-pkg-card__title">{pkg.title}</span>
                      {pkg.badge ? (
                        <span className="ai-topup-pkg-card__badge-wrap">
                          <V2Badge tone="brand">{pkg.badge}</V2Badge>
                        </span>
                      ) : null}
                    </div>

                    <div className="ai-topup-pkg-card__tokens">
                      <strong className="sk-num ai-topup-pkg-card__tokens-num">{tokenStr}</strong>
                      <span className="ai-topup-pkg-card__tokens-label">توکن واقعی</span>
                    </div>

                    <div className="ai-topup-pkg-card__price">
                      <span className="sk-num ai-topup-pkg-card__price-num">
                        {formatIntegerString(pkg.amountRial)}
                      </span>
                      <span className="ai-topup-pkg-card__price-unit">ریال در درگاه</span>
                    </div>

                    <div className="ai-topup-pkg-card__desc">{pkg.description}</div>
                  </button>
                );
              })}
            </div>

            {/* گزینهٔ سهمیه دلخواه توکن */}
            <div
              className={`ai-topup-custom-card ${useCustom ? 'ai-topup-custom-card--active' : ''}`}
              onClick={handleSelectCustom}
            >
              <div className="ai-topup-custom-card__header">
                <div className="ai-topup-pkg-card__radio" aria-hidden="true">
                  {useCustom ? <span className="ai-topup-pkg-card__radio-inner" /> : null}
                </div>
                <span className="ai-topup-custom-card__title">سهمیهٔ دلخواه توکن</span>
                <span className="ai-topup-custom-card__tag">بدون سقف</span>
              </div>

              <div className="ai-topup-custom-card__body">
                <div
                  className={`ai-topup-input-wrapper ${customError ? 'ai-topup-input-wrapper--error' : ''}`}
                >
                  <label htmlFor={customInputId} className="ai-visually-hidden">
                    تعداد توکن دلخواه
                  </label>
                  <input
                    id={customInputId}
                    ref={customInputRef}
                    type="text"
                    inputMode="numeric"
                    className="ai-topup-custom-input"
                    placeholder="مثلاً ۱۰٬۰۰۰٬۰۰۰"
                    value={formatThousandsPersian(customDigits)}
                    onChange={handleCustomChange}
                    onFocus={() => setUseCustom(true)}
                    aria-invalid={customError ? 'true' : 'false'}
                  />
                  <span className="ai-topup-input-unit" aria-hidden="true">
                    توکن
                  </span>
                </div>

                {customError ? (
                  <div className="ai-topup-field-msg ai-topup-field-msg--error" role="alert">
                    <Icon name="warn" size="sm" />
                    <span>{customError}</span>
                  </div>
                ) : (
                  <div className="ai-topup-field-msg ai-topup-field-msg--hint">
                    <Icon name="info" size="sm" />
                    <span>حداقل سهمیهٔ شارژ ۵٬۰۰۰٬۰۰۰ توکن است.</span>
                  </div>
                )}

                {useCustom && customValid ? (
                  <div className="ai-topup-live-tokens" role="status">
                    <Icon name="check" size="sm" />
                    <span>
                      سهمیهٔ دریافتی:{' '}
                      <strong className="sk-num">
                        {formatIntegerString(customTokens.toString())}
                      </strong>{' '}
                      توکن | مبلغ پرداختی در درگاه بانکی:{' '}
                      <strong className="sk-num">
                        {formatIntegerString((customTokens * 2n).toString())}
                      </strong>{' '}
                      ریال
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* خلاصه پیش از پرداخت */}
        {validatedAmount && canPay ? (
          <div className="ai-topup-summary">
            <div className="ai-topup-summary__head">
              <span className="ai-topup-summary__title">خلاصه صورت‌حساب شارژ</span>
              <span className="ai-topup-summary__type">
                {useCustom ? 'شارژ دلخواه' : selectedPkg?.title}
              </span>
            </div>

            <div className="ai-topup-summary__grid">
              <div className="ai-topup-summary__row ai-topup-summary__row--accent">
                <span className="ai-topup-summary__label">سهمیهٔ توکن دریافتی:</span>
                <span className="ai-topup-summary__val sk-num">
                  {formatIntegerString(activeTokens.toString())} توکن واقعی
                </span>
              </div>
              <div className="ai-topup-summary__row">
                <span className="ai-topup-summary__label">مبلغ پرداختی در درگاه بانکی:</span>
                <span className="ai-topup-summary__val sk-num">
                  {formatIntegerString(validatedAmount.amountRial)} ریال
                </span>
              </div>
            </div>

            <div className="ai-topup-summary__foot">
              <Icon name="shield" size="sm" />
              <span>پرداخت امن الکترونیک از طریق شبکهٔ شاپرک (درگاه رسمی زرین‌پال)</span>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
